import { Task, Epic, Project } from '../types';

/**
 * Default system-wide cost per hour if no rates are set
 */
export const DEFAULT_COST_PER_HOUR = 75;

/**
 * Get the effective cost per hour for a task using cascade inheritance:
 * Task → Epic → Project → Default
 * 
 * @param task - The task to calculate cost for
 * @param epic - Optional epic the task belongs to
 * @param project - The project the task belongs to
 * @returns The effective cost per hour in dollars
 */
export function getEffectiveCostPerHour(
  task: Task,
  epic?: Epic | null,
  project?: Project | null
): number {
  // Check task level first
  if (task.costPerHour !== undefined && task.costPerHour !== null) {
    return task.costPerHour;
  }
  
  // Then check epic level
  if (epic?.costPerHour !== undefined && epic.costPerHour !== null) {
    return epic.costPerHour;
  }
  
  // Then check project level
  if (project?.costPerHour !== undefined && project.costPerHour !== null) {
    return project.costPerHour;
  }
  
  // Finally fall back to default
  return DEFAULT_COST_PER_HOUR;
}

/**
 * Calculate the estimated cost for a task based on estimated hours and cost per hour
 */
export function calculateEstimatedCost(
  task: Task,
  epic?: Epic | null,
  project?: Project | null
): number {
  const costPerHour = getEffectiveCostPerHour(task, epic, project);
  const hours = task.estimatedHours || 0;
  return hours * costPerHour;
}

/**
 * Calculate the actual cost for a task based on time logged and cost per hour
 */
export function calculateActualCost(
  task: Task,
  epic?: Epic | null,
  project?: Project | null
): number {
  const costPerHour = getEffectiveCostPerHour(task, epic, project);
  const loggedHours = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
  return loggedHours * costPerHour;
}

/**
 * Get the source of the cost per hour rate for display purposes
 */
export function getCostPerHourSource(
  task: Task,
  epic?: Epic | null,
  project?: Project | null
): 'task' | 'epic' | 'project' | 'default' {
  if (task.costPerHour !== undefined && task.costPerHour !== null) {
    return 'task';
  }
  
  if (epic?.costPerHour !== undefined && epic.costPerHour !== null) {
    return 'epic';
  }
  
  if (project?.costPerHour !== undefined && project.costPerHour !== null) {
    return 'project';
  }
  
  return 'default';
}

/**
 * Format currency values
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format currency with cents
 */
export function formatCurrencyDetailed(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
